import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { FileCheck2, ArrowRight, Hash, Package, Mail, Phone, Loader2, ArrowLeft, MapPin, X } from "lucide-react";
import api from "../lib/api";
import { Button } from "../components/ui/button";

const _fmtD = (iso) => { try { return iso ? new Date(iso).toLocaleDateString("de-DE") : ""; } catch { return ""; } };

export default function OffeneZulassungenPage() {
    const [items, setItems] = useState(null);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [siteFilter, setSiteFilter] = useState("all");

    useEffect(() => {
        api.get("/dealer/disposition/open-registrations")
            .then((r) => { setItems(r.data?.items || []); setSummary(r.data?.summary || null); })
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    }, []);

    const URG = {
        red: { dot: "bg-red-500", chip: "bg-red-100 text-red-800 border-red-300", label: "überfällig" },
        amber: { dot: "bg-amber-500", chip: "bg-amber-100 text-amber-800 border-amber-300", label: "fällig" },
        green: { dot: "bg-emerald-500", chip: "bg-emerald-100 text-emerald-800 border-emerald-300", label: "im Plan" },
    };

    const sites = useMemo(() => {
        const s = new Set((items || []).map((i) => i.site_code).filter(Boolean));
        return Array.from(s).sort();
    }, [items]);

    const filtered = useMemo(
        () => (items || []).filter((i) => siteFilter === "all" || i.site_code === siteFilter),
        [items, siteFilter],
    );

    // Aug-2026 (User-Wunsch): Test-/Fehl-Vorgänge aus der Liste entfernen
    const removeItem = async (it) => {
        if (!window.confirm(`„${it.title}" aus den offenen Zulassungen entfernen?\n\nDer Vorgang wird dauerhaft ausgeblendet (z. B. Testvorgang oder keine Zulassung nötig).`)) return;
        try {
            await api.post(`/dealer/disposition/open-registrations/${it.id}/dismiss`);
            setItems((prev) => (prev || []).filter((x) => x.id !== it.id));
            toast.success("Vorgang entfernt");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Entfernen fehlgeschlagen");
        }
    };

    return (
        <div className="min-h-screen bg-gray-50" data-testid="offene-zulassungen-page">
            <div className="bg-[#111827] text-white">
                <div className="max-w-5xl mx-auto px-4 md:px-8 py-8">
                    <Link to="/dealer/disposition" className="text-xs text-gray-400 hover:text-white inline-flex items-center gap-1" data-testid="back-to-disposition">
                        <ArrowLeft className="w-3 h-3" /> Zurück zur Disposition
                    </Link>
                    <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight flex items-center gap-3">
                        <FileCheck2 className="w-9 h-9 text-[#FFCC00]" /> Offene Zulassungen
                    </h1>
                    <p className="text-sm text-gray-400 mt-2 truncate max-w-full">
                        Verkaufte & reservierte Fahrzeuge, deren Zulassung noch nicht abgeschlossen ist — Unterlagen-Fortschritt und Kundendaten auf einen Blick.
                    </p>
                </div>
            </div>

            <div className="max-w-5xl mx-auto px-4 md:px-8 py-6">
                {sites.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="offene-zulassungen-site-filter">
                        <span className="text-xs font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Standort:</span>
                        <button
                            onClick={() => setSiteFilter("all")}
                            className={`text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm border transition-colors ${siteFilter === "all" ? "bg-[#111827] text-[#FFCC00] border-[#111827]" : "bg-white text-gray-700 border-gray-300 hover:border-[#111827]"}`}
                            data-testid="offene-zulassungen-site-all"
                        >
                            Alle
                        </button>
                        {sites.map((sc) => (
                            <button
                                key={sc}
                                onClick={() => setSiteFilter(sc)}
                                className={`text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm border transition-colors ${siteFilter === sc ? "bg-[#FFCC00] text-[#111827] border-[#FFCC00]" : "bg-white text-gray-700 border-gray-300 hover:border-[#111827]"}`}
                                data-testid={`offene-zulassungen-site-${sc}`}
                            >
                                {sc}
                            </button>
                        ))}
                    </div>
                )}

                {summary && (items?.length || 0) > 0 && (
                    <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="offene-zulassungen-summary">
                        <span className="inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-sm border border-red-300 bg-red-50 text-red-800"><span className="w-2 h-2 rounded-full bg-red-500" /> {summary.red || 0} überfällig (&gt;14 T.)</span>
                        <span className="inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-sm border border-amber-300 bg-amber-50 text-amber-800"><span className="w-2 h-2 rounded-full bg-amber-500" /> {summary.amber || 0} fällig (8–14 T.)</span>
                        <span className="inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-sm border border-emerald-300 bg-emerald-50 text-emerald-800"><span className="w-2 h-2 rounded-full bg-emerald-500" /> {summary.green || 0} im Plan (≤7 T.)</span>
                    </div>
                )}

                {loading ? (
                    <div className="flex items-center gap-2 text-gray-500 p-8"><Loader2 className="w-5 h-5 animate-spin" /> Lädt …</div>
                ) : filtered.length === 0 ? (
                    <div className="bg-white border border-gray-200 rounded-sm p-8 text-center text-gray-500" data-testid="offene-zulassungen-empty">
                        Keine offenen Zulassungen. Alle verkauften Fahrzeuge sind zugelassen. 🎉
                    </div>
                ) : (
                    <ul className="space-y-3" data-testid="offene-zulassungen-list">
                        {filtered.map((it) => (
                            <li key={it.id} className={`bg-white border border-gray-200 border-l-4 ${it.urgency === "red" ? "border-l-red-500" : it.urgency === "amber" ? "border-l-amber-500" : "border-l-emerald-500"} rounded-sm p-3 flex items-start gap-3 hover:shadow-sm transition-all`} data-testid={`offene-zulassung-row-${it.id}`}>
                                <Link to={`/listing/${it.id}`} target="_blank" rel="noopener noreferrer" className="w-16 h-16 shrink-0 bg-gray-100 rounded-sm overflow-hidden block">
                                    {it.image ? <img src={it.image} alt={it.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-gray-300"><Package className="w-6 h-6" /></div>}
                                </Link>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-bold text-sm text-[#111827] truncate">{it.title}</span>
                                        {it.internal_number && <span className="text-[10px] font-mono-num font-bold px-1.5 py-0.5 rounded-sm bg-[#111827] text-[#FFCC00]">{it.internal_number}</span>}
                                        {it.site_code && <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-gray-100 border border-gray-300 rounded-sm text-gray-700">{it.site_code}</span>}
                                        {it.status_label && <span className="text-[9px] uppercase font-black tracking-wider px-1.5 py-0.5 rounded-sm bg-orange-100 text-orange-800 border border-orange-300">{it.status_label}</span>}
                                        <span className={`text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded-sm border ${it.started ? "bg-amber-50 text-amber-800 border-amber-300" : "bg-gray-50 text-gray-500 border-gray-300"}`} data-testid={`offene-zulassung-progress-${it.id}`}>
                                            Unterlagen {it.docs_done}/{it.docs_total || 0}
                                        </span>
                                        {it.days_open != null && (
                                            <span className={`text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded-sm border inline-flex items-center gap-1 ${(URG[it.urgency] || URG.green).chip}`} data-testid={`offene-zulassung-urgency-${it.id}`}>
                                                <span className={`w-1.5 h-1.5 rounded-full ${(URG[it.urgency] || URG.green).dot}`} />
                                                {it.days_open} T. · {(URG[it.urgency] || URG.green).label}
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-[11px] text-gray-600 mt-0.5 flex items-center gap-x-3 gap-y-0.5 flex-wrap">
                                        {it.vin && <span className="font-mono-num inline-flex items-center gap-0.5"><Hash className="w-3 h-3" />{it.vin}</span>}
                                        {it.license_plate && <span className="font-mono-num">Kennz. {it.license_plate}</span>}
                                        {it.provider && <span>Zulassungsdienst: <strong>{it.provider}</strong></span>}
                                        {it.handover_date && <span>Übergabe: {_fmtD(it.handover_date)}</span>}
                                    </div>
                                    <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                                        {it.customer?.name && (
                                            <span className="inline-flex items-center gap-1.5 text-[10px] px-2 py-1 rounded-sm bg-amber-50 border border-amber-300 text-amber-900" data-testid={`offene-zulassung-customer-${it.id}`}>
                                                <strong>Käufer:</strong> {it.customer.name}
                                                {it.customer.phone && <a href={`tel:${it.customer.phone}`} className="text-blue-700"><Phone className="w-3 h-3" /></a>}
                                                {it.customer.email && <a href={`mailto:${it.customer.email}`} className="text-blue-700"><Mail className="w-3 h-3" /></a>}
                                            </span>
                                        )}
                                        {it.seller?.name && (
                                            <span className="inline-flex items-center gap-1.5 text-[10px] px-2 py-1 rounded-sm bg-sky-50 border border-sky-300 text-sky-900" data-testid={`offene-zulassung-seller-${it.id}`}>
                                                <strong>Verkäufer:</strong> {it.seller.name}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="shrink-0 flex flex-col items-end gap-1.5">
                                    <Link
                                        to={`/edit-listing/${it.id}?focus=disposition&action=registration`}
                                        target="_blank" rel="noopener noreferrer"
                                        className="inline-flex items-center justify-center gap-1 text-[10px] uppercase font-bold tracking-wider px-2 py-1.5 rounded-sm bg-[#FFCC00] text-[#111827] hover:bg-[#e6b800] transition-colors"
                                        data-testid={`offene-zulassung-open-${it.id}`}
                                    >
                                        Zulassung <ArrowRight className="w-3 h-3" />
                                    </Link>
                                    <button type="button" onClick={() => removeItem(it)}
                                        className="inline-flex items-center gap-1 text-[9px] uppercase font-bold tracking-wider px-2 py-1 rounded-sm border border-gray-300 text-gray-500 hover:border-red-400 hover:text-red-600 transition-colors"
                                        title="Vorgang aus der Liste entfernen (Test / keine Zulassung nötig)"
                                        data-testid={`offene-zulassung-remove-${it.id}`}>
                                        <X className="w-3 h-3" /> Entfernen
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}
